/**
 * Banner Feito no Brasil — scroll bidirecional.
 * Texto (+ zoom opcional) no progress 0→1; após passar o banner, tudo fixo em 1.
 */
function gmClamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

/** Altura do 2º navbar PDP (ATC dock), publicada em --gm-pdp-atc-dock-height. */
function gmFeitoDockHeight() {
  return (
    parseFloat(
      getComputedStyle(document.body).getPropertyValue('--gm-pdp-atc-dock-height')
    ) || 0
  );
}

/**
 * @param {DOMRectReadOnly} rect — painel [data-feito-reveal-root]
 * @param {number} vh
 * @param {number} endFactor — topo do painel em endFactor×vh = progress 1
 * @param {number} startFactor — progress 0 até topo atingir startFactor×vh
 */
function gmFeitoScrollProgress(rect, vh, endFactor, startFactor = 1) {
  const startTop = vh * startFactor;
  const endTop = vh * endFactor;
  const range = startTop - endTop;
  if (range <= 0) {
    return rect.top <= endTop ? 1 : 0;
  }
  return gmClamp((startTop - rect.top) / range, 0, 1);
}

/**
 * Progresso para painéis altos: referência no centro (onde fica o copy).
 * @param {DOMRectReadOnly} rect
 * @param {number} vh
 * @param {number} startFactor — copyY inicia em startFactor×vh
 * @param {number} endFactor — copyY termina em endFactor×vh
 */
function gmFeitoTallScrollProgress(
  rect,
  vh,
  startFactor = 0.92,
  endFactor = 0.42,
  dockH = 0
) {
  const copyY = rect.top + rect.height * 0.5;
  const startY = vh * startFactor;
  const endY = dockH + (vh - dockH) * endFactor;
  const range = startY - endY;
  if (range <= 0) {
    return copyY <= endY ? 1 : 0;
  }
  return gmClamp((startY - copyY) / range, 0, 1);
}

/**
 * Staggered: 0 quando o topo do bloco entra pela base da viewport;
 * 1 quando o topo atinge o centro da tela (50vh).
 */
function gmFeitoElementEnterProgress(rect, vh) {
  if (rect.bottom <= 0) return 1;
  if (rect.top >= vh) return 0;

  const startY = vh;
  const endY = vh * 0.5;
  const range = startY - endY;
  if (range <= 0) {
    return rect.top <= endY ? 1 : 0;
  }
  return gmClamp((startY - rect.top) / range, 0, 1);
}

if (!customElements.get('gm-feito-banner')) {
  class GmFeitoBanner extends HTMLElement {
    connectedCallback() {
      this._root =
        this.closest('[data-feito-reveal-root]') ||
        this.closest('.gm-mobile-feito__body');
      this._reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (!this._root) return;

      this._viewportCentered = this.dataset.scrollReference === 'viewport-center';
      this._media = this._root.querySelector('.gm-bolsas-banner__media, .gm-feito-banner__media') || this._root;
      const category = this.closest('.gm-collection-category--bolsas');
      this._mobileIntroState = category?.querySelector('gm-feito-banner') === this ? 'waiting' : null;
      if (this._root.classList.contains('gm-bolsas-banner--dual')) {
        this._photos = this._root.querySelectorAll('.gm-teaser-panel__visual');
        this._photoResizeObserver = new ResizeObserver(() => this._measureMobilePhotos());
        this._photos.forEach(photo => this._photoResizeObserver.observe(photo));
        this._measureMobilePhotos();
      }
      this._staggered =
        this.dataset.revealMode === 'staggered' ||
        this._root?.classList.contains('gm-feito-banner--reveal-staggered');
      this._whatFits = Boolean(this.closest('gm-what-fits'));
      this._whatFitsCta = this._whatFits
        ? this.querySelector('.gm-feito-banner__cta-button, .gm-feito-banner__link')
        : null;

      if (this._reduced) {
        this._applyReduced();
        return;
      }

      this._startFactor = parseFloat(this.dataset.startFactor || '1');
      this._endFactor = parseFloat(this.dataset.endFactor || '0.28');
      this._rightDelay = parseFloat(this.dataset.rightDelay || '0.06');
      this._ctaDelay = parseFloat(this.dataset.ctaDelay || '0.32');
      this._maxZoom = parseFloat(this.dataset.zoom || '0.15');
      this._raf = null;

      this._onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._update();
        });
      };
      this._onChromeChange = () => this._onScroll();

      if (this._mobileIntroState) {
        this._media.addEventListener('load', this._onScroll, true);
        this._media.addEventListener('error', this._onScroll, true);
      }
      this._update();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      window.addEventListener('gm-chrome-change', this._onChromeChange);
      if (this._viewportCentered) {
        window.visualViewport?.addEventListener('resize', this._onScroll);
        window.visualViewport?.addEventListener('scroll', this._onScroll);
        this._resizeObserver = new ResizeObserver(this._onScroll);
        this._resizeObserver.observe(this._media);
        const header = document.getElementById('header-component');
        if (header) this._resizeObserver.observe(header);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      window.removeEventListener('gm-chrome-change', this._onChromeChange);
      window.visualViewport?.removeEventListener('resize', this._onScroll);
      window.visualViewport?.removeEventListener('scroll', this._onScroll);
      this._resizeObserver?.disconnect();
      this._photoResizeObserver?.disconnect();
      this._media?.removeEventListener('load', this._onScroll, true);
      this._media?.removeEventListener('error', this._onScroll, true);
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._introRaf) cancelAnimationFrame(this._introRaf);
      this._root?.style.setProperty('--gm-feito-img-scale', '1');
    }

    _applyFrame(p, pRight, pCta, scale) {
      this.style.setProperty('--gm-feito-p', String(p));
      this.style.setProperty('--gm-feito-p-right', String(pRight));
      this.style.setProperty('--gm-feito-p-cta', String(pCta));
      this._root?.style.setProperty('--gm-feito-img-scale', String(scale));
    }

    _applyReduced() {
      if (this._staggered) {
        this.querySelectorAll(
          '.gm-feito-banner__title--from-left, .gm-feito-banner__title--from-right, .gm-feito-banner__link--fade, .gm-feito-banner__cta-button--fade'
        ).forEach((el) => {
          if (el.classList.contains('gm-feito-banner__title--from-right')) {
            el.style.setProperty('--gm-feito-p-right', '1');
          } else if (
            el.classList.contains('gm-feito-banner__link--fade') ||
            el.classList.contains('gm-feito-banner__cta-button--fade')
          ) {
            el.style.setProperty('--gm-feito-p-cta', '1');
          } else {
            el.style.setProperty('--gm-feito-p', '1');
          }
        });
      } else {
        this._applyFrame(1, 1, 1, 1);
      }
      this._root?.style.setProperty('--gm-feito-img-scale', '1');
    }

    _setElementProgress(el, varName, progress) {
      el.style.setProperty(varName, String(Number(progress.toFixed(4))));
    }

    _updateStaggered() {
      const vh = window.innerHeight;

      const left = this.querySelector('.gm-feito-banner__title--from-left');
      const cta = this.querySelector(
        '.gm-feito-banner__cta-button--fade, .gm-feito-banner__link--fade'
      );
      const right = this.querySelector('.gm-feito-banner__title--from-right');

      if (left) {
        this._setElementProgress(
          left,
          '--gm-feito-p',
          gmFeitoElementEnterProgress(left.getBoundingClientRect(), vh)
        );
      }
      if (cta) {
        this._setElementProgress(
          cta,
          '--gm-feito-p-cta',
          gmFeitoElementEnterProgress(cta.getBoundingClientRect(), vh)
        );
      }
      if (right) {
        this._setElementProgress(
          right,
          '--gm-feito-p-right',
          gmFeitoElementEnterProgress(right.getBoundingClientRect(), vh)
        );
      }

      this._root?.style.setProperty('--gm-feito-img-scale', '1');
    }

    _measureMobilePhotos() {
      if (window.innerWidth > 749 || this._photos?.length !== 2) return;
      const top = this._root.getBoundingClientRect().top;
      const firstBottom = this._photos[0].getBoundingClientRect().bottom - top;
      const secondTop = this._photos[1].getBoundingClientRect().top - top;
      this._root.style.setProperty('--gm-bolsas-photo-1-bottom', `${firstBottom.toFixed(2)}px`);
      this._root.style.setProperty('--gm-bolsas-photo-2-top', `${secondTop.toFixed(2)}px`);
    }

    _visibleArea() {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
      const header = document.getElementById('header-component');
      const headerBottom = header
        ? header.getBoundingClientRect().bottom
        : viewportTop + (parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0);
      const usefulTop = gmClamp(headerBottom, viewportTop, viewportBottom);
      return { top: usefulTop, bottom: viewportBottom };
    }

    _updateMobileIntro() {
      if (!this._mobileIntroState) return false;
      if (window.innerWidth > 749) {
        if (this._mobileIntroState === 'running') {
          cancelAnimationFrame(this._introRaf);
          this._introRaf = null;
          this._mobileIntroState = 'complete';
        }
        return false;
      }
      if (this._mobileIntroState === 'running') return true;
      if (this._mobileIntroState === 'complete') {
        this._applyFrame(1, 1, 1, 1);
        return true;
      }

      this._applyFrame(0, 0, 0, 1);
      const area = this._visibleArea();
      const rect = this._media.getBoundingClientRect();
      const imagesReady = [...this._media.querySelectorAll('img')].every(image => image.complete);
      if (rect.bottom > area.top && rect.top < area.bottom && imagesReady) {
        this._playMobileIntro();
      }
      return true;
    }

    _playMobileIntro() {
      this._mobileIntroState = 'running';
      const start = performance.now();
      const ease = progress => 1 - (1 - progress) ** 3;
      const tick = now => {
        const progress = gmClamp((now - start) / 1100, 0, 1);
        const right = gmClamp((progress - this._rightDelay) / Math.max(0.01, 1 - this._rightDelay), 0, 1);
        this._applyFrame(ease(progress), ease(right), 1, 1);
        if (progress < 1) {
          this._introRaf = requestAnimationFrame(tick);
        } else {
          this._introRaf = null;
          this._mobileIntroState = 'complete';
        }
      };
      this._introRaf = requestAnimationFrame(tick);
    }

    _imageProgress() {
      const area = this._visibleArea();

      return gmFeitoTallScrollProgress(
        this._media.getBoundingClientRect(),
        area.bottom,
        this._startFactor,
        this._endFactor,
        area.top
      );
    }

    _update() {
      if (this._updateMobileIntro()) return;
      if (this._staggered) {
        this._updateStaggered();
        return;
      }

      const rect = this._root.getBoundingClientRect();
      const vh = window.innerHeight;
      const dockH = gmFeitoDockHeight();
      const isCopyZone = this._root?.classList.contains(
        'gm-feito-banner--copy-zone-teaser'
      );
      const isTall = rect.height > vh * 1.02;
      // O que cabe mobile: slide completo quando o botão cruza 50vh.
      const mobileWhatFits = this._whatFits && window.innerWidth <= 749;
      const copyTarget =
        mobileWhatFits && this._whatFitsCta ? this._whatFitsCta : this;
      const p = this._viewportCentered
        ? this._imageProgress()
        : isCopyZone
        ? gmFeitoTallScrollProgress(
            copyTarget.getBoundingClientRect(),
            vh,
            mobileWhatFits ? 0.9 : this._startFactor,
            mobileWhatFits ? 0.5 : this._endFactor,
            mobileWhatFits ? 0 : dockH
          )
        : isTall
          ? gmFeitoTallScrollProgress(
              rect,
              vh,
              this._startFactor,
              this._endFactor,
              dockH
            )
          : gmFeitoScrollProgress(
              rect,
              vh,
              this._endFactor,
              this._startFactor
            );
      const rightSpan = Math.max(0.01, 1 - this._rightDelay);
      const pRight = gmClamp((p - this._rightDelay) / rightSpan, 0, 1);
      const ctaSpan = Math.max(0.01, 1 - this._ctaDelay);
      const pCta = gmClamp((p - this._ctaDelay) / ctaSpan, 0, 1);
      const scale = this._maxZoom > 0 ? 1 + p * this._maxZoom : 1;

      this._applyFrame(
        Number(p.toFixed(4)),
        Number(pRight.toFixed(4)),
        Number(pCta.toFixed(4)),
        Number(scale.toFixed(4))
      );
    }
  }

  customElements.define('gm-feito-banner', GmFeitoBanner);
}
